import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { Spacing, Radius } from '@/constants/spacing';
import { Elevation } from '@/constants/elevation';
import { Typography } from '@/constants/typography';
import { StockSearchSummary } from '@/types/search-history.types';
import { getStockConversationSummary } from '@/services/history/searchHistoryService';
import { useWatchlist } from '@/hooks/useWatchlist';

interface StockHistoryDropdownProps {
  onStockSelect: (summary: StockSearchSummary) => void;
  onClose?: () => void;
  visible?: boolean;
  position?: { y: number; height: number };
}

export const StockHistoryDropdown: React.FC<StockHistoryDropdownProps> = ({
  onStockSelect,
  onClose,
  visible: controlledVisible,
  position,
}) => {
  const [internalVisible, setInternalVisible] = useState(false);
  const visible = controlledVisible !== undefined ? controlledVisible : internalVisible;
  const [summaries, setSummaries] = useState<StockSearchSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastLoadedAtRef = useRef<number | null>(null);

  // Watchlist functionality
  const { watchlist, addToWatchlist, removeFromWatchlist, isInWatchlist } = useWatchlist();

  // Fetch stock history when dropdown opens (with simple cache TTL)
  useEffect(() => {
    if (visible) {
      const now = Date.now();
      // Check if we have recent data (within 30 seconds) and non-empty summaries
      if (
        lastLoadedAtRef.current &&
        now - lastLoadedAtRef.current < 30000 &&
        summaries.length > 0
      ) {
        console.log(
          'Using cached history summaries (age:',
          Math.round((now - lastLoadedAtRef.current) / 1000),
          's)'
        );
        return;
      }
      console.log('Fetching fresh history summaries');
      fetchStockHistory();
    }
  }, [visible, summaries.length]); // Added summaries.length dependency

  const fetchStockHistory = async () => {
    setLoading(true);
    setError(null);
    const result = await getStockConversationSummary();
    if (result.success && result.summaries) {
      setSummaries(result.summaries);
      lastLoadedAtRef.current = Date.now();
    } else if (!result.success && result.error) {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    const result = await getStockConversationSummary();
    if (result.success && result.summaries) {
      setSummaries(result.summaries);
      lastLoadedAtRef.current = Date.now();
    } else if (!result.success && result.error) {
      setError(result.error);
    }
    setRefreshing(false);
  };

  const handleStockSelect = (summary: StockSearchSummary) => {
    if (onClose) {
      onClose();
    } else {
      setInternalVisible(false);
    }
    onStockSelect(summary);
  };

  const handleWatchlistToggle = async (ticker: string, e: any) => {
    e.stopPropagation(); // Prevent stock selection when tapping star

    if (isInWatchlist(ticker)) {
      const success = await removeFromWatchlist(ticker);
      if (success) {
        Alert.alert('Removed', `${ticker} removed from watchlist`);
      } else {
        Alert.alert('Error', `Failed to remove ${ticker} from watchlist`);
      }
    } else {
      const success = await addToWatchlist(ticker, ticker); // Using ticker as company name
      if (success) {
        Alert.alert('Added', `${ticker} added to watchlist`);
      } else {
        Alert.alert('Error', `Failed to add ${ticker} to watchlist`);
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  if (!visible) return null;

  const dropdownTop = position ? position.y + position.height + 8 : 150;

  return (
    <>
      {/* Backdrop */}
      <Pressable
        style={styles.backdrop}
        onPress={() => {
          if (onClose) {
            onClose();
          } else {
            setInternalVisible(false);
          }
        }}
      />

      {/* Dropdown */}
      <View style={[styles.dropdown, { top: dropdownTop }]}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.tickerIcon}>$</Text>
            <Text style={styles.headerTitle}>Stock Search History</Text>
          </View>
          <View style={styles.headerActions}>
            <Pressable
              accessibilityRole="button"
              onPress={handleRefresh}
              style={styles.headerButton}
            >
              {refreshing ? (
                <ActivityIndicator size="small" color={Colors.ai.primary} />
              ) : (
                <Ionicons name="refresh" size={18} color={Colors.text.tertiary} />
              )}
            </Pressable>
            <Pressable
              accessibilityRole="button"
              onPress={() => {
                if (onClose) {
                  onClose();
                } else {
                  setInternalVisible(false);
                }
              }}
              style={styles.closeButton}
            >
              <Ionicons name="close" size={22} color={Colors.text.tertiary} />
            </Pressable>
          </View>
        </View>

        {/* Error Row */}
        {error && (
          <View style={styles.errorRow}>
            <Ionicons name="alert-circle-outline" size={16} color={Colors.status.error} />
            <Text style={styles.errorText}>{error}</Text>
            <Pressable onPress={handleRefresh} style={styles.retryInline}>
              <Text style={styles.retryInlineText}>Retry</Text>
            </Pressable>
          </View>
        )}
        {/* Content - Scrollable */}
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={true}
          nestedScrollEnabled={true}
        >
          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="small" color={Colors.ai.primary} />
              <Text style={styles.loadingText}>Loading history...</Text>
            </View>
          ) : summaries.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyTitle}>No Stock Searches Yet</Text>
              <Text style={styles.emptySubtitle}>Search for a stock to see it here</Text>
            </View>
          ) : (
            summaries.map(summary => (
              <Pressable
                key={summary.stock_symbol}
                onPress={() => handleStockSelect(summary)}
                style={styles.stockItem}
                accessibilityRole="button"
                accessibilityLabel={`$${summary.stock_symbol}, ${summary.search_count} searches, last ${formatDate(summary.last_searched_at)}`}
              >
                <View style={styles.stockInfo}>
                  <View style={styles.stockHeader}>
                    <Text style={styles.stockSymbol}>${summary.stock_symbol}</Text>
                    <Text style={styles.searchCount}>
                      {summary.search_count} {summary.search_count === 1 ? 'search' : 'searches'}
                    </Text>
                  </View>
                  <Text style={styles.timestamp}>{formatDate(summary.last_searched_at)}</Text>
                </View>

                {/* Watchlist star button */}
                <Pressable
                  onPress={e => handleWatchlistToggle(summary.stock_symbol, e)}
                  style={styles.starButton}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <Ionicons
                    name={isInWatchlist(summary.stock_symbol) ? 'star' : 'star-outline'}
                    size={20}
                    color={
                      isInWatchlist(summary.stock_symbol) ? Colors.ai.accent : Colors.text.tertiary
                    }
                  />
                </Pressable>

                <Ionicons name="chevron-forward" size={16} color={Colors.text.tertiary} />
              </Pressable>
            ))
          )}
        </ScrollView>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 999,
  },
  dropdown: {
    position: 'absolute',
    left: Spacing.xl,
    right: Spacing.xl,
    backgroundColor: Colors.surface.raised,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: Colors.border.default,
    zIndex: 1000,
    overflow: 'hidden',
    ...Elevation.level3,
    shadowOpacity: 0.32,
    shadowRadius: 24,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: Spacing.md,
    paddingHorizontal: Spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerButton: {
    padding: Spacing.xs,
    borderRadius: Radius.sm,
    marginLeft: Spacing.xs,
  },
  tickerIcon: {
    ...Typography.caption,
    fontWeight: '800',
    color: Colors.ai.primary,
    paddingHorizontal: Spacing.xs,
    paddingVertical: 2,
    borderRadius: Radius.sm,
    borderWidth: 1,
    borderColor: Colors.ai.primary,
    marginRight: Spacing.sm,
  },
  headerTitle: {
    ...Typography.subtitle,
    color: Colors.text.primary,
    fontWeight: '600',
  },
  closeButton: {
    padding: Spacing.xs,
    marginLeft: Spacing.xs,
  },
  scrollView: {
    maxHeight: 320,
  },
  scrollContent: {
    flexGrow: 1,
  },
  loadingContainer: {
    paddingVertical: Spacing.xl,
    paddingHorizontal: Spacing.lg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    ...Typography.bodySmall,
    color: Colors.text.secondary,
    marginTop: Spacing.xs,
  },
  errorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  errorText: {
    flex: 1,
    ...Typography.bodySmall,
    color: Colors.status.error,
  },
  retryInline: {
    paddingVertical: Spacing.xs,
    paddingHorizontal: Spacing.sm,
    borderRadius: Radius.sm,
    borderWidth: 1,
    borderColor: Colors.ai.primary,
    backgroundColor: Colors.surface.overlay,
  },
  retryInlineText: {
    ...Typography.bodySmall,
    color: Colors.ai.primary,
    fontWeight: '600',
  },
  emptyContainer: {
    paddingVertical: Spacing.xl,
    paddingHorizontal: Spacing.lg,
    alignItems: 'center',
  },
  emptyTitle: {
    ...Typography.subtitle,
    color: Colors.text.secondary,
  },
  emptySubtitle: {
    ...Typography.bodySmall,
    color: Colors.text.tertiary,
    marginTop: Spacing.xs,
    textAlign: 'center',
  },
  stockItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: Spacing.md,
    paddingHorizontal: Spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  stockInfo: {
    flex: 1,
  },
  stockHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: Spacing.xs,
  },
  stockSymbol: {
    ...Typography.subtitle,
    color: Colors.ai.primary,
    marginRight: Spacing.sm,
  },
  searchCount: {
    ...Typography.caption,
    color: Colors.text.tertiary,
  },
  timestamp: {
    ...Typography.caption,
    color: Colors.text.tertiary,
  },
  starButton: {
    padding: Spacing.xs,
    marginRight: Spacing.sm,
  },
});
